/**
 * Presentation helpers for the conformity predicate visualization. Pure (no JSX);
 * inherits the ontology-tree vocabulary (norm chips, unit expansion) from
 * din276Tree so the look matches the DIN-276 Kategorien view.
 */
import type { DinNode } from "../administration/din276/din276Tree";
import type { Cmp, ConformityStatus, Tri, UnitResolver } from "./conformityTypes";

/** Comparison operator → compact symbol. */
export const CMP_LABEL: Record<Cmp, string> = {
  ge: "≥",
  le: "≤",
  gt: ">",
  lt: "<",
  eq: "=",
  ne: "≠",
  in: "∈",
  exists: "∃",
};

/** Comparison operator → human tooltip (German, matching the rest of the legal UI). */
export const CMP_TOOLTIP: Record<Cmp, string> = {
  ge: "größer oder gleich",
  le: "kleiner oder gleich",
  gt: "größer als",
  lt: "kleiner als",
  eq: "gleich",
  ne: "ungleich",
  in: "enthalten in",
  exists: "vorhanden (Präsenzprüfung — nie unbestimmt)",
};

/** Source → reference where the datum lives. */
export const SOURCE_LABEL: Record<string, string> = {
  asset: "Asset",
  part: "Teil",
  building: "Gebäude",
  obligation: "Pflicht",
};

/** Norm chip colour bucket from the source string (same buckets as din276 tree). */
export function normKindClass(source?: string): string {
  const s = (source || "").toUpperCase();
  if (/H?EPN|HEN|EN 14351|EN 16034|EN 1125|EN 179/.test(s)) return "hen";
  if (s.startsWith("EN ")) return "en";
  if (s.startsWith("DIN ")) return "din";
  if (s.startsWith("ASR")) return "asr";
  if (s.includes("DGUV")) return "dguv";
  if (s.includes("GEFMA")) return "gefma";
  if (s.startsWith("TRI")) return "tri";
  return "other";
}

/** Truth value → css tone class. */
export function tvClass(tv: Tri): string {
  return tv === "T" ? "tv-t" : tv === "F" ? "tv-f" : "tv-u";
}

/** Conformity status → css tone class. */
export function outcomeClass(o: ConformityStatus): string {
  switch (o) {
    case "Compliant":
      return "out-compliant";
    case "Violated":
      return "out-violated";
    case "NotDeterminable":
      return "out-undetermined";
    default:
      return "out-na";
  }
}

/**
 * Build a unit resolver from the DIN-276 ontology (byCode map from buildTree).
 * Units are reliably known for part attributes — the enclosing Quant supplies the
 * `typeCode` (e.g. "K.HANDLAUF") and the body atom the attribute name. Asset /
 * building attributes have no ontology typeCode, so they resolve to null and
 * simply render without a unit.
 */
export function dinUnitResolver(byCode: Record<string, DinNode>): UnitResolver {
  return (typeCode: string | undefined, attr: string): string | null => {
    if (!typeCode) return null;
    const node = byCode[typeCode];
    if (!node) return null;
    const a = node.attrs.find((x) => x.name === attr);
    return a?.unit ?? null;
  };
}
